<script lang="ts" setup>
import type { InputDateProps } from '@nuxt/ui';
import type { DateRange, DateValue } from 'reka-ui';
import type { Temporal } from 'temporal-polyfill';
import type { ButtonProps } from '#layers/ui/components/Button.vue';
import { CalendarDate, isSameDay, toCalendarDate } from '@internationalized/date';
import { computed, ref, shallowRef, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { calendarDateConverter, formatPlainDate, nowDate, shortPlainDateFormatOptions, useUserLocale } from '#layers/base/public';
import DateRangePickerPanel from './DateRangePickerPanel.vue';

interface Props extends Pick<ButtonProps, 'color' | 'size' | 'disabled'>, Pick<InputDateProps, 'variant'> {
	readonly?: boolean;
	block?: boolean;
}

const { block = true, disabled, readonly } = defineProps<Props>();

defineSlots<{
	trigger: (props: {
		rangeFieldDisplay: string;
		isRangeEmpty: boolean;
		canClearRange: boolean;
		clearRange: () => void;
	}) => any;
}>();

const startDate = defineModel<Temporal.PlainDate | null>('startDate');
const endDate = defineModel<Temporal.PlainDate | null>('endDate');

const { t } = useI18n();
const { formatLocale } = useUserLocale();

const todayCalendarDate = computed(() => {
	const now = nowDate();
	return new CalendarDate(now.year, now.month, now.day);
});

const calendarStartDate = computed(() => calendarDateConverter.toCalendar(startDate.value));
const calendarEndDate = computed(() => calendarDateConverter.toCalendar(endDate.value));

const startPlaceholderRef = shallowRef<DateValue>(calendarStartDate.value ?? todayCalendarDate.value);
const endPlaceholderRef = shallowRef<DateValue>(calendarEndDate.value ?? todayCalendarDate.value);

/**
 * Synthetic start boundary when only an end date is set — highlights everything up to the end date.
 * Derived from the earlier of the two placeholders so the span stays small (≤ ~2 months) regardless of era;
 * a fixed epoch would either invert the range for pre-epoch end dates or trigger the y1 browser hang.
 */
const syntheticOpenStart = computed(() => {
	const start = toCalendarDate(startPlaceholderRef.value);
	const end = toCalendarDate(endPlaceholderRef.value);
	const earliest = start.compare(end) < 0 ? start : end;

	return earliest.set({ day: 1 }).subtract({ months: 2 });
});

const displayRange = computed<DateRange>(() => {
	const start = calendarStartDate.value;
	const end = calendarEndDate.value;

	if (start && end) {
		return { start, end };
	}

	if (start && !end) {
		const openEndExclusive = todayCalendarDate.value.add({ days: 1 });
		if (start.compare(openEndExclusive) >= 0) return { start, end: start.add({ days: 1 }) };
		return { start, end: openEndExclusive };
	}

	if (!start && end) return { start: syntheticOpenStart.value, end };

	return { start: undefined, end: undefined };
});

/**
 * Reka snaps each panel's placeholder onto the range start on every range change. Since
 * syntheticOpenStart is derived from the placeholders, accepting that snap would create a
 * feedback loop. Reject it when the incoming value already equals the range start; real user
 * navigation carries a different day-of-month and passes through.
 */
const startPlaceholder = computed<DateValue>({
	get: () => startPlaceholderRef.value,
	set: (value: DateValue) => {
		const rangeStart = displayRange.value.start;
		const date = toCalendarDate(value);
		if (!startDate.value && endDate.value && rangeStart && isSameDay(date, rangeStart)) return;
		startPlaceholderRef.value = date;
	},
});

const endPlaceholder = computed<DateValue>({
	get: () => endPlaceholderRef.value,
	set: (value: DateValue) => {
		const rangeStart = displayRange.value.start;
		const date = toCalendarDate(value);
		if (rangeStart && isSameDay(date, rangeStart)) return;
		endPlaceholderRef.value = date;
	},
});

const allowNonContiguousRanges = computed(() => !(startDate.value && !endDate.value));

const isRangeEmpty = computed(() => !startDate.value && !endDate.value);
const canClearRange = computed(() => !disabled && !readonly && !isRangeEmpty.value);

const isStartSetToNowDisabled = computed(() => {
	const end = calendarEndDate.value;
	return end != null && end.compare(todayCalendarDate.value) < 0;
});

const isEndSetToNowDisabled = computed(() => {
	const start = calendarStartDate.value;
	return start != null && start.compare(todayCalendarDate.value) > 0;
});

const rangeFieldDisplay = computed(() => {
	const start = formatPlainDate(startDate.value, formatLocale.value, shortPlainDateFormatOptions);
	const end = formatPlainDate(endDate.value, formatLocale.value, shortPlainDateFormatOptions);

	if (start && end) return t('date_range_picker.summary_range', { start, end });
	if (end) return t('date_range_picker.summary_before_end', { end });
	if (start) return t('date_range_picker.summary_from_start', { start });
	return t('date_range_picker.select_date');
});

function onStartCalendarUpdate(value: DateRange | null) {
	if (!value) return;
	const date = value.start ?? value.end;
	if (!date || isStartDateUnavailable(date)) return;
	startDate.value = calendarDateConverter.toTemporal(toCalendarDate(date));
}

function onEndCalendarUpdate(value: DateRange | null) {
	if (!value) return;
	const date = value.end ?? value.start;
	if (!date || isEndDateUnavailable(date)) return;
	endDate.value = calendarDateConverter.toTemporal(toCalendarDate(date));
}

function setStartToNow() {
	if (isStartSetToNowDisabled.value) return;
	startDate.value = nowDate();
}

function setEndToNow() {
	if (isEndSetToNowDisabled.value) return;
	endDate.value = nowDate();
}

function clearRange() {
	clearStartDate();
	clearEndDate();
}

function clearStartDate() {
	startDate.value = null;
	startPlaceholderRef.value = todayCalendarDate.value;
}

function clearEndDate() {
	endDate.value = null;
	endPlaceholderRef.value = todayCalendarDate.value;
}

function isFutureDate(date: DateValue) {
	return date.compare(todayCalendarDate.value) > 0;
}

function isStartDateUnavailable(date: DateValue) {
	return isFutureDate(date) || (calendarEndDate.value != null && date.compare(calendarEndDate.value) > 0);
}

function isEndDateUnavailable(date: DateValue) {
	return isFutureDate(date) || (calendarStartDate.value != null && date.compare(calendarStartDate.value) < 0);
}

const rangePopoverOpen = ref(false);

watch(rangePopoverOpen, (open) => {
	if (!open) return;
	startPlaceholderRef.value = calendarStartDate.value ?? todayCalendarDate.value;
	endPlaceholderRef.value = calendarEndDate.value ?? todayCalendarDate.value;
});
</script>

<template>
	<div class="min-w-0" :class="block ? 'w-full' : 'w-fit'">
		<DPopover
			v-model:open="rangePopoverOpen"
			:disabled
			:content="{
				side: 'bottom' as const,
				align: 'end' as const,
				onOpenAutoFocus: (e: Event) => e.preventDefault(),
			}"
			:ui="{ content: 'p-4' }"
		>
			<slot
				name="trigger"
				:range-field-display
				:is-range-empty
				:can-clear-range
				:clear-range
			/>

			<template #content>
				<div data-testid="date-range-popover" class="flex gap-4">
					<DateRangePickerPanel
						v-model:placeholder="startPlaceholder"
						panel="start"
						:readonly
						:selected-date="startDate ?? null"
						:display-range
						:allow-non-contiguous-ranges
						:is-date-unavailable="isStartDateUnavailable"
						:is-set-to-now-disabled="isStartSetToNowDisabled"
						:variant
						:size
						:disabled
						@clear="clearStartDate"
						@set-to-now="setStartToNow"
						@update:calendar="onStartCalendarUpdate"
					/>
					<DateRangePickerPanel
						v-model:placeholder="endPlaceholder"
						panel="end"
						:readonly
						:selected-date="endDate ?? null"
						:display-range
						:allow-non-contiguous-ranges
						:is-date-unavailable="isEndDateUnavailable"
						:is-set-to-now-disabled="isEndSetToNowDisabled"
						:variant
						:size
						:disabled
						@clear="clearEndDate"
						@set-to-now="setEndToNow"
						@update:calendar="onEndCalendarUpdate"
					/>
				</div>
			</template>
		</DPopover>
	</div>
</template>
